/**
 * Tenant-level currency configuration.
 *
 * The active utility's ISO currency code comes from the backend branding fetch
 * (`/utilityconfigurations/{tenant}/branding`) — TenantProvider stores it here as
 * soon as branding loads, so non-React code (Money, formatCurrency) can resolve
 * the tenant currency without a hook. Symbol and decimal precision are derived
 * from the ISO code via Intl, so onboarding a utility with a new currency needs
 * no frontend change.
 *
 * NGN is the defined fallback while branding is loading, unavailable, or has no
 * currency configured — existing utilities keep today's behavior.
 */
export const FALLBACK_CURRENCY = "NGN";

let tenantCurrency = FALLBACK_CURRENCY;

export const setTenantCurrency = (code?: string | null): void => {
	const trimmed = code?.trim();
	if (trimmed && trimmed.length === 3) tenantCurrency = trimmed.toUpperCase();
};

export const getTenantCurrency = (): string => tenantCurrency;

const symbolCache = new Map<string, string>();

/** Currency symbol for an ISO code ("NGN" → "₦"); the code itself when Intl doesn't know it. */
export const getCurrencySymbol = (code?: string | null): string => {
	const currency = (code ?? tenantCurrency).toUpperCase();
	const cached = symbolCache.get(currency);
	if (cached !== undefined) return cached;

	let symbol = currency;
	try {
		const parts = new Intl.NumberFormat("en", {
			style: "currency",
			currency,
			currencyDisplay: "narrowSymbol",
		}).formatToParts(0);
		symbol = parts.find(part => part.type === "currency")?.value ?? currency;
	} catch {
		// Unknown/invalid code — the code itself is the symbol.
	}
	symbolCache.set(currency, symbol);
	return symbol;
};

/** ISO decimal precision of a currency (NGN → 2, XOF → 0); 2 when unknown. */
export const getCurrencyDecimals = (code?: string | null): number => {
	const currency = (code ?? tenantCurrency).toUpperCase();
	try {
		return new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2;
	} catch {
		return 2;
	}
};
